// NOTE: Wrapper import will be reintroduced after legacy component removal below
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Lock, Unlock, AlertTriangle, Shield, CheckCircle, XCircle,
  ArrowLeft, ArrowRight, RefreshCw, BookOpen, Settings, Trash2,
  Cpu, Monitor, Network, FileText, Eye, EyeOff, Download,
  ChevronRight, Clock, Play, Pause, RotateCcw, Search
} from 'lucide-react';
import RealisticOSSimulator from './RealisticOSSimulator';
import { getChallengeById } from '../../api/games.api';

const LegacyBrowserExtensionMalwareSimulator = ({
  scenarios = [],
  onComplete,
  challenge,
  estimatedTime = 15
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [userDecisions, setUserDecisions] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackContent, setFeedbackContent] = useState(null);
  const [simulationStarted, setSimulationStarted] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [browserExtensions, setBrowserExtensions] = useState([]);
  const [taskManagerProcesses, setTaskManagerProcesses] = useState([]);
  const [currentAction, setCurrentAction] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0, accuracy: 100 });
  const navigate = useNavigate();

  // Initialize challenge steps from scenarios or challenge data (and fetch from backend if needed)
  useEffect(() => {
    // console.log('BrowserExtensionMalwareSimulator received:', { scenarios, challenge });

    // Helper: normalize steps from any source
    const normalizeSteps = (rawSteps = []) =>
      rawSteps
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .map(step => ({
          id: step.step_id || step.id,
          title: step.title,
          description: step.description,
          type: step.type || 'action',
          content: step.content,
          time_limit: step.time_limit,
          points: step.points,
          hints: step.hints
        }));

    // Prefer steps already present on challenge
    let initialSteps;
    if (challenge?.steps && Array.isArray(challenge.steps) && challenge.steps.length > 0) {
      initialSteps = normalizeSteps(challenge.steps);
      setSteps(initialSteps);
      setProgress({ completed: 0, total: initialSteps.length, accuracy: 100 });
    } else if (scenarios && scenarios.length > 0) {
      initialSteps = normalizeSteps(scenarios);
      setSteps(initialSteps);
      setProgress({ completed: 0, total: initialSteps.length, accuracy: 100 });
    } else {
      // Fallback steps for "The Malicious Browser Extension" challenge
      initialSteps = [
        {
          id: 'step-1',
          title: 'Initial System Scan',
          description: 'Perform an initial scan of the suspicious browser extension files',
          type: 'scan',
          action: 'Open File Explorer and navigate to browser extensions folder',
          correctAction: 'navigate_to_extensions'
        },
        {
          id: 'step-2',
          title: 'Extension Size Analysis',
          description: 'What is suspicious about the extension\'s file size?',
          type: 'analysis',
          question: 'What is suspicious about the extension\'s file size?',
          options: [
            'It\'s much larger than typical ad blockers',
            'It\'s smaller than expected',
            'The size is normal',
            'Size doesn\'t matter for extensions'
          ],
          correctAnswer: 0,
          explanation: 'Malicious extensions often have unusually large file sizes due to embedded malware or cryptocurrency miners.'
        },
        {
          id: 'step-3',
          title: 'Behavior Analysis',
          description: 'What suspicious behavior do you notice?',
          type: 'analysis',
          question: 'What suspicious behavior do you notice?',
          options: [
            'Excessive CPU usage',
            'Unusual network connections',
            'Injecting unwanted ads',
            'All of the above'
          ],
          correctAnswer: 3,
          explanation: 'Malicious extensions typically exhibit multiple suspicious behaviors including high resource usage, network activity, and content injection.'
        },
        {
          id: 'step-4',
          title: 'Resource Usage Analysis',
          description: 'Open Task Manager to analyze the extension\'s resource usage',
          type: 'action',
          action: 'Check Task Manager for high CPU usage processes',
          correctAction: 'open_task_manager'
        },
        {
          id: 'step-5',
          title: 'Network Analysis',
          description: 'Check the extension\'s network connections in Task Manager',
          type: 'analysis',
          action: 'Analyze network connections and data usage',
          correctAction: 'analyze_network'
        },
        {
          id: 'step-6',
          title: 'Malware Classification',
          description: 'What type of malware is this extension likely to be?',
          type: 'classification',
          question: 'What type of malware is this extension likely to be?',
          options: [
            'Adware',
            'Spyware',
            'Cryptominer',
            'Ransomware'
          ],
          correctAnswer: 2,
          explanation: 'Given the high CPU usage and network activity, this extension is most likely a cryptocurrency miner.'
        },
        {
          id: 'step-7',
          title: 'Threat Remediation',
          description: 'Remove the malicious extension through browser settings',
          type: 'remediation',
          action: 'Remove the malicious extension from browser',
          correctAction: 'remove_extension'
        }
      ];
    }

    // If we have challenge id but no steps, try fetching from backend
    const tryFetchSteps = async () => {
      try {
        if (!challenge || steps.length > 0) return;
        const challengeId = challenge.id || challenge.challenge_id || challenge._id;
        if (!challengeId) return;
        const baseUrl = (import.meta && import.meta.env && import.meta.env.VITE_API_URL) || process.env.REACT_APP_API_URL || '';
        const url = `${baseUrl}/api/challenges/${challengeId}`;
        // console.log('Fetching challenge from backend:', url);
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to fetch challenge ${challengeId}`);
        const data = await res.json();
        const fetchedSteps = normalizeSteps(data?.steps || data?.challenge?.steps || data?.challengeSteps || []);
        if (fetchedSteps.length > 0) {
          setSteps(fetchedSteps);
          setProgress({ completed: 0, total: fetchedSteps.length, accuracy: 100 });
        }
      } catch (err) {
        console.warn('Could not fetch steps from backend, using fallback if available.', err);
      }
    };

    // Set fallback or existing steps immediately when defined
    if (typeof initialSteps !== 'undefined') {
      setSteps(initialSteps);
      setProgress({ completed: 0, total: initialSteps.length, accuracy: 100 });
    }

    // Kick off fetch (non-blocking)
    tryFetchSteps();

    // Initialize browser extensions with malicious extension
    setBrowserExtensions([
      {
        id: 'malicious-extension',
        name: 'Super Ad Blocker Pro',
        version: '2.1.4',
        size: '15.7 MB',
        permissions: ['Read and change all your data on all websites', 'Access your data on all sites'],
        isMalicious: true,
        suspiciousBehaviors: [
          'Unusually large file size (15.7 MB vs typical 1-2 MB)',
          'Excessive CPU usage (45-60%)',
          'High network activity',
          'Injecting additional ads instead of blocking them'
        ],
        malwareType: 'Cryptominer'
      },
      {
        id: 'legitimate-extension-1',
        name: 'LastPass',
        version: '4.92.0',
        size: '2.1 MB',
        permissions: ['Access your data on all sites'],
        isMalicious: false
      },
      {
        id: 'legitimate-extension-2',
        name: 'uBlock Origin',
        version: '1.52.2',
        size: '1.8 MB',
        permissions: ['Read and change all your data on all websites'],
        isMalicious: false
      }
    ]);

    // Initialize Task Manager processes
    setTaskManagerProcesses([
      {
        id: 'chrome-main',
        name: 'chrome.exe',
        cpu: '12%',
        memory: '245 MB',
        network: 'Low',
        isNormal: true
      },
      {
        id: 'malicious-extension-process',
        name: 'Super Ad Blocker Pro',
        cpu: '58%',
        memory: '89 MB',
        network: 'High (2.3 MB/s)',
        isNormal: false,
        isMalicious: true
      },
      {
        id: 'system-process',
        name: 'svchost.exe',
        cpu: '3%',
        memory: '45 MB',
        network: 'None',
        isNormal: true
      }
    ]);
    // Auto-start the simulation to avoid a non-interactive screen
    setSimulationStarted(true);
    setCurrentAction('scan');

  }, [scenarios, challenge]);

  // Timer for challenge duration
  useEffect(() => {
    if (simulationStarted && !showFeedback) {
      const timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [simulationStarted, showFeedback]);

  const handleUserAction = (action, stepId) => {
    const currentStepData = steps[currentStep];
    const stepIdToUse = stepId || currentStepData.id;

    // console.log('🔧 handleUserAction called:', { action, stepId, currentStep, currentStepData });

    // Determine if action is correct based on step type and action
    let isCorrect = false;
    let shouldProgress = false;

    // Step 1: Initial System Scan - Any action is correct to progress
    if (currentStep === 0 && (action === 'navigate_to_extensions' || action === 'open_task_manager')) {
      isCorrect = true;
      shouldProgress = true;
    }
    // Step 4: Resource Usage Analysis - Opening task manager is correct
    else if (currentStep === 3 && action === 'open_task_manager') {
      isCorrect = true;
      shouldProgress = true;
    }
    // Step 5: Network Analysis - Analyzing processes is correct
    else if (currentStep === 4 && (action === 'analyze_malicious_process' || action === 'analyze_normal_process')) {
      isCorrect = true;
      shouldProgress = true;
    }
    // Step 7: Threat Remediation - Removing extension is correct
    else if (currentStep === 6 && action === 'remove_extension') {
      isCorrect = true;
      shouldProgress = true;
    }
    // Any other action
    else {
      isCorrect = false;
      shouldProgress = false;
    }

    // console.log('🔧 Action validation result:', { isCorrect, shouldProgress, action, currentStep });

    const decision = {
      stepId: stepIdToUse,
      action,
      correct: isCorrect,
      timestamp: Date.now(),
      timeSpent: timeElapsed
    };

    setUserDecisions(prev => ({
      ...prev,
      [stepIdToUse]: decision
    }));

    // Update progress
    const completed = Object.keys({ ...userDecisions, [stepIdToUse]: decision }).length;
    const correctDecisions = Object.values({ ...userDecisions, [stepIdToUse]: decision })
      .filter(d => d.correct).length;

    setProgress({
      completed,
      total: steps.length,
      accuracy: Math.round((correctDecisions / completed) * 100)
    });

    // Show feedback
    setFeedbackContent({
      isCorrect,
      title: isCorrect ? 'Correct Action!' : 'Incorrect Action!',
      explanation: isCorrect ?
        'Great! You took the correct action for this step.' :
        'That action is not appropriate for this step. Try a different approach.',
      action: currentStepData.action,
      stepTitle: currentStepData.title,
      shouldProgress
    });

    // Auto-progress if the action was correct and should progress
    if (shouldProgress && isCorrect) {
      // console.log('🔧 Auto-progressing to next step...');
      // Show feedback briefly then auto-progress
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackContent(null);

        if (currentStep < steps.length - 1) {
          // console.log('🔧 Moving to step:', currentStep + 1);
          setCurrentStep(currentStep + 1);
          setCurrentAction(null);
        } else {
          // Challenge completed
          const correctDecisions = Object.values({ ...userDecisions, [stepIdToUse]: decision }).filter(d => d.correct).length;
          const summary = {
            correct: correctDecisions,
            total: steps.length,
            score: Math.round((correctDecisions / steps.length) * 100),
            timeElapsed,
            decisions: { ...userDecisions, [stepIdToUse]: decision },
            accuracy: Math.round((correctDecisions / (Object.keys({ ...userDecisions, [stepIdToUse]: decision }).length)) * 100)
          };

          if (onComplete) {
            onComplete(summary);
          }
        }
      }, 1500); // Show feedback for 1.5 seconds
    } else {
      setShowFeedback(true);
    }
  };

  const handleAnswer = (answerIndex, stepId) => {
    const currentStepData = steps[currentStep];
    const isCorrect = answerIndex === currentStepData.correctAnswer;

    const decision = {
      stepId: stepId || currentStepData.id,
      answer: answerIndex,
      correct: isCorrect,
      timestamp: Date.now(),
      timeSpent: timeElapsed
    };

    setUserDecisions(prev => ({
      ...prev,
      [stepId || currentStepData.id]: decision
    }));

    // Update progress
    const completed = Object.keys({ ...userDecisions, [stepId || currentStepData.id]: decision }).length;
    const correctDecisions = Object.values({ ...userDecisions, [stepId || currentStepData.id]: decision })
      .filter(d => d.correct).length;

    setProgress({
      completed,
      total: steps.length,
      accuracy: Math.round((correctDecisions / completed) * 100)
    });

    // Show feedback
    setFeedbackContent({
      isCorrect,
      title: isCorrect ? 'Correct Answer!' : 'Incorrect Answer!',
      explanation: currentStepData.explanation,
      selectedAnswer: currentStepData.options[answerIndex],
      correctAnswer: currentStepData.options[currentStepData.correctAnswer],
      stepTitle: currentStepData.title,
      shouldProgress: isCorrect
    });

    // Auto-progress if the answer was correct
    if (isCorrect) {
      // Show feedback briefly then auto-progress
      setShowFeedback(true);
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackContent(null);

        if (currentStep < steps.length - 1) {
          setCurrentStep(currentStep + 1);
          setCurrentAction(null);
        } else {
          // Challenge completed
          const correctDecisions = Object.values({ ...userDecisions, [stepId || currentStepData.id]: decision }).filter(d => d.correct).length;
          const summary = {
            correct: correctDecisions,
            total: steps.length,
            score: Math.round((correctDecisions / steps.length) * 100),
            timeElapsed,
            decisions: { ...userDecisions, [stepId || currentStepData.id]: decision },
            accuracy: Math.round((correctDecisions / (Object.keys({ ...userDecisions, [stepId || currentStepData.id]: decision }).length)) * 100)
          };

          if (onComplete) {
            onComplete(summary);
          }
        }
      }, 1500); // Show feedback for 1.5 seconds
    } else {
      setShowFeedback(true);
    }
  };

  const handleNextStep = () => {
    setShowFeedback(false);
    setFeedbackContent(null);

    // Manual progression - only used for incorrect answers or manual navigation
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setCurrentAction(null);
    } else {
      // Challenge completed
      const correctDecisions = Object.values(userDecisions).filter(d => d.correct).length;
      const summary = {
        correct: correctDecisions,
        total: steps.length,
        score: Math.round((correctDecisions / steps.length) * 100),
        timeElapsed,
        decisions: userDecisions,
        accuracy: progress.accuracy
      };

      if (onComplete) {
        onComplete(summary);
      }
    }
  };

  const handleLearnMore = () => {
    const meta = challenge?.metadata;

    // Support older challenges where metadata was a plain URL string
    if (typeof meta === 'string' && meta.startsWith('http')) {
      window.open(meta, '_blank', 'noopener,noreferrer');
      return;
    }

    // Support newer JSON metadata objects with explicit URLs
    if (meta && typeof meta === 'object') {
      const url =
        meta.learnMoreUrl ||
        meta.learn_more_url ||
        meta.docUrl ||
        meta.doc_url ||
        meta.url;

      if (typeof url === 'string' && url.startsWith('http')) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    }

    // Fallback to arena-based navigation
    navigate('/learning/module/malware_ransomware?topic=browser_security');
  };

  const startSimulation = () => {
    setSimulationStarted(true);
    setCurrentAction('scan');
  };

  const renderStepContent = () => {
    const step = steps[currentStep];
    if (!step) return null;

    // Handle different content structures from database
    const content = step.content || {};
    const action = step.action || content.action;
    const question = step.question || content.question;
    const options = step.options || content.options;
    const correctAction = step.correctAction || content.correctAction;
    const correctAnswer = step.correctAnswer !== undefined ? step.correctAnswer : content.correct_answer;

    switch (step.type) {
      case 'scan':
      case 'action':
      case 'simulation': // Handle 'simulation' type from database
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">Action Required:</h3>
              <p className="text-blue-700">{action || step.description}</p>
            </div>

            {/* Show different buttons based on current step */}
            {currentStep === 0 ? (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    // console.log('🔧 Browser Settings button clicked');
                    handleUserAction('navigate_to_extensions', step.id);
                  }}
                  className="flex items-center justify-center p-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Settings size={20} className="mr-2" />
                  Open Browser Settings
                </button>

                <button
                  onClick={() => {
                    // console.log('🔧 Task Manager button clicked');
                    handleUserAction('open_task_manager', step.id);
                  }}
                  className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Cpu size={20} className="mr-2" />
                  Open Task Manager
                </button>
              </div>
            ) : currentStep === 3 ? (
              <button
                onClick={() => handleUserAction('open_task_manager', step.id)}
                className="w-full flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Cpu size={20} className="mr-2" />
                Open Task Manager
              </button>
            ) : (
              <div className="text-center text-gray-600 py-4">
                Complete the action in the simulator interface above.
              </div>
            )}
          </div>
        );

      case 'analysis':
      case 'classification':
        return (
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-semibold text-yellow-800 mb-2">Question:</h3>
              <p className="text-yellow-700">{question || step.description}</p>
            </div>

            {options && options.length > 0 && (
              <div className="space-y-2">
                {options.map((option, index) => (
                  <button
                    key={index}
                    onClick={() => handleAnswer(index, step.id)}
                    className="w-full p-3 text-left bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-gray-900"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        );

      case 'remediation':
        return (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="font-semibold text-red-800 mb-2">Remediation Required:</h3>
              <p className="text-red-700">{action || step.description}</p>
            </div>

            <button
              onClick={() => handleUserAction('remove_extension', step.id)}
              className="w-full flex items-center justify-center p-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 size={20} className="mr-2" />
              Remove Malicious Extension
            </button>
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-2">Step {currentStep + 1}:</h3>
              <p className="text-gray-700">{step.description}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleUserAction('complete_step', step.id)}
                className="flex items-center justify-center p-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <CheckCircle size={20} className="mr-2" />
                Complete Step
              </button>

              <button
                onClick={() => handleUserAction('skip_step', step.id)}
                className="flex items-center justify-center p-4 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowRight size={20} className="mr-2" />
                Skip Step
              </button>
            </div>
          </div>
        );
    }
  };

  const renderBrowserExtensions = () => (
    <div className="bg-white rounded-lg border border-gray-300 shadow-sm">
      {/* Windows-style title bar */}
      <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <span className="text-sm font-medium text-gray-700">Extensions</span>
        </div>
        <div className="flex items-center space-x-1">
          <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
            <span className="text-xs">−</span>
          </button>
          <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
            <span className="text-xs">□</span>
          </button>
          <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
            <span className="text-xs">×</span>
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="p-4">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-800">Installed Extensions</h3>
            <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
              Add Extension
            </button>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <button className="hover:text-blue-600">All</button>
            <button className="hover:text-blue-600">Enabled</button>
            <button className="hover:text-blue-600">Disabled</button>
          </div>
        </div>

        {/* Extensions list */}
        <div className="space-y-1">
          {browserExtensions.map(ext => (
            <div
              key={ext.id}
              className={`p-3 rounded-lg border hover:shadow-sm transition-all ${ext.isMalicious
                ? 'border-red-300 bg-red-50 hover:bg-red-100'
                : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {/* Extension icon */}
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ext.isMalicious ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                    {ext.isMalicious ? (
                      <AlertTriangle size={20} className="text-red-600" />
                    ) : (
                      <Shield size={20} className="text-blue-600" />
                    )}
                  </div>

                  {/* Extension details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-800">{ext.name}</h4>
                      {ext.isMalicious && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertTriangle size={10} className="mr-1" />
                          Suspicious
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 space-x-4">
                      <span>Version {ext.version}</span>
                      <span>Size: {ext.size}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {ext.permissions[0]}
                    </div>
                  </div>
                </div>

                {/* Extension controls */}
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                    Details
                  </button>
                  {ext.isMalicious ? (
                    <button
                      onClick={() => {
                        // console.log('🔧 Remove extension button clicked');
                        handleUserAction('remove_extension', 'step-7');
                      }}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center space-x-1"
                      title="Remove malicious extension"
                    >
                      <Trash2 size={14} />
                      <span>Remove</span>
                    </button>
                  ) : (
                    <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTaskManager = () => (
    <div className="bg-white rounded-lg border border-gray-300 shadow-sm">
      {/* Windows-style title bar */}
      <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="flex space-x-1">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <span className="text-sm font-medium text-gray-700">Task Manager</span>
        </div>
        <div className="flex items-center space-x-1">
          <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
            <span className="text-xs">−</span>
          </button>
          <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
            <span className="text-xs">□</span>
          </button>
          <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
            <span className="text-xs">×</span>
          </button>
        </div>
      </div>

      {/* Task Manager tabs */}
      <div className="bg-gray-50 border-b border-gray-300 px-4 py-2">
        <div className="flex items-center space-x-6 text-sm">
          <button className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
            Processes
          </button>
          <button className="px-3 py-1 text-gray-600 hover:text-gray-800">Performance</button>
          <button className="px-3 py-1 text-gray-600 hover:text-gray-800">App history</button>
          <button className="px-3 py-1 text-gray-600 hover:text-gray-800">Startup</button>
          <button className="px-3 py-1 text-gray-600 hover:text-gray-800">Users</button>
          <button className="px-3 py-1 text-gray-600 hover:text-gray-800">Details</button>
          <button className="px-3 py-1 text-gray-600 hover:text-gray-800">Services</button>
        </div>
      </div>

      {/* Content area */}
      <div className="p-0">
        {/* Search and filter bar */}
        <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search processes..."
                className="pl-8 pr-4 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
              />
              <Search size={14} className="absolute left-2 top-1.5 text-gray-400" />
            </div>
            <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">
              End task
            </button>
          </div>
          <div className="text-sm text-gray-600">
            {taskManagerProcesses.length} processes
          </div>
        </div>

        {/* Processes table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Name</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">CPU</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Memory</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Disk</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Network</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">GPU</th>
              </tr>
            </thead>
            <tbody>
              {taskManagerProcesses.map(process => (
                <tr
                  key={process.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${process.isMalicious ? 'bg-red-50 hover:bg-red-100' : ''
                    }`}
                  onClick={() => {
                    if (process.isMalicious) {
                      handleUserAction('analyze_malicious_process', 'step-5');
                    } else {
                      handleUserAction('analyze_normal_process', 'step-5');
                    }
                  }}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      {/* Process icon */}
                      <div className={`w-8 h-8 rounded flex items-center justify-center ${process.isMalicious ? 'bg-red-100' : 'bg-blue-100'
                        }`}>
                        {process.isMalicious ? (
                          <AlertTriangle size={16} className="text-red-600" />
                        ) : (
                          <Cpu size={16} className="text-blue-600" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">{process.name}</div>
                        <div className="text-xs text-gray-500">Running</div>
                      </div>
                      {process.isMalicious && (
                        <AlertTriangle size={14} className="text-red-600" />
                      )}
                    </div>
                  </td>
                  <td className={`py-3 px-4 ${process.cpu.includes('%') && parseInt(process.cpu) > 30
                    ? 'text-red-600 font-semibold'
                    : 'text-gray-700'
                    }`}>
                    {process.cpu}
                  </td>
                  <td className="py-3 px-4 text-gray-700">{process.memory}</td>
                  <td className="py-3 px-4 text-gray-700">0%</td>
                  <td className={`py-3 px-4 ${process.network.includes('High')
                    ? 'text-red-600 font-semibold'
                    : 'text-gray-700'
                    }`}>
                    {process.network}
                  </td>
                  <td className="py-3 px-4 text-gray-700">0%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderFeedbackModal = () => {
    if (!showFeedback || !feedbackContent) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-6">
          <div className="flex items-center mb-4">
            {feedbackContent.isCorrect ? (
              <CheckCircle size={24} className="text-green-600 mr-3" />
            ) : (
              <XCircle size={24} className="text-red-600 mr-3" />
            )}
            <h3 className={`text-lg font-semibold ${feedbackContent.isCorrect ? 'text-green-800' : 'text-red-800'
              }`}>
              {feedbackContent.title}
            </h3>
          </div>

          <p className="text-gray-700 mb-4">{feedbackContent.explanation}</p>

          {feedbackContent.selectedAnswer && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-600">
                <strong>Your answer:</strong> {feedbackContent.selectedAnswer}
              </p>
              {!feedbackContent.isCorrect && (
                <p className="text-sm text-gray-600 mt-1">
                  <strong>Correct answer:</strong> {feedbackContent.correctAnswer}
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={handleLearnMore}
              className="flex items-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <BookOpen size={16} className="mr-2" />
              Learn More
            </button>
            <button
              onClick={handleNextStep}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {feedbackContent?.shouldProgress && feedbackContent?.isCorrect ?
                (currentStep < steps.length - 1 ? 'Continue' : 'Complete Challenge') :
                'Try Again'
              }
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Auto-started; retain fallback screen only if explicitly required
  if (!simulationStarted) {
    return null;
  }

  const currentStepData = steps[currentStep];
  const completedSteps = Object.keys(userDecisions).length;

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 relative">
      {/* Windows Desktop Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 opacity-90 pointer-events-none"></div>

      {/* Windows Taskbar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-600 h-12 flex items-center px-4 z-50">
        <div className="flex items-center space-x-4">
          <button className="w-8 h-8 bg-gray-700 hover:bg-gray-600 rounded flex items-center justify-center">
            <Globe size={16} className="text-white" />
          </button>
          <button className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm">
            Extensions
          </button>
          <button className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-white text-sm">
            Task Manager
          </button>
        </div>
        <div className="ml-auto flex items-center space-x-2 text-white text-sm">
          <span>100%</span>
          <div className="w-1 h-4 bg-white"></div>
          <span>12:34 PM</span>
        </div>
      </div>

      <div className="relative z-10 flex flex-col lg:flex-row min-h-[100dvh] pt-4 lg:pt-8 pb-12">
        {/* Left Side - Simulator Interface */}
        <div className="flex-1 p-3 sm:p-6">
          <div className="bg-gray-100 rounded-lg shadow-2xl h-full border border-gray-400">
            {/* Windows-style title bar */}
            <div className="bg-gray-200 border-b border-gray-400 px-4 py-2 flex items-center justify-between rounded-t-lg">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <span className="text-sm font-medium text-gray-700">Windows Security Simulator</span>
              </div>
              <div className="flex items-center space-x-1">
                <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-300 rounded">
                  <span className="text-xs">−</span>
                </button>
                <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-300 rounded">
                  <span className="text-xs">□</span>
                </button>
                <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-300 rounded">
                  <span className="text-xs">×</span>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6 bg-white rounded-b-lg h-full overflow-y-auto">
              {/* Browser Extensions Section */}
              {renderBrowserExtensions()}

              {/* Task Manager Section */}
              {renderTaskManager()}
            </div>
          </div>
        </div>

        {/* Right Side - Challenge Progress */}
        <div className="w-full lg:w-96 bg-white border-t lg:border-t-0 lg:border-l border-gray-300 shadow-lg">
          {/* Windows-style title bar for right panel */}
          <div className="bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <span className="text-sm font-medium text-gray-700">Challenge Progress</span>
            </div>
            <div className="flex items-center space-x-1">
              <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
                <span className="text-xs">−</span>
              </button>
              <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
                <span className="text-xs">□</span>
              </button>
              <button className="w-5 h-5 flex items-center justify-center hover:bg-gray-200 rounded">
                <span className="text-xs">×</span>
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 overflow-y-auto h-full max-h-[38vh] lg:max-h-none">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Challenge Assistant</h3>
                <p className="text-sm text-gray-600">
                  Follow the step-by-step instructions to complete the malware detection challenge.
                </p>
              </div>

              {/* Current Action */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Clock size={16} className="text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">
                    Step {currentStep + 1}: Action Required
                  </span>
                </div>
                <p className="text-blue-700 text-sm mb-3">
                  {currentStepData?.description}
                </p>

                {/* Waiting indicator */}
                <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3">
                  <div className="flex items-center">
                    <AlertTriangle size={16} className="text-yellow-600 mr-2" />
                    <span className="text-yellow-800 text-sm">
                      Waiting for you to complete this action in the simulator...
                    </span>
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-gray-50 rounded-lg p-4">
                {renderStepContent()}
              </div>

              {/* Challenge Steps List */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Challenge Steps</h4>

                {/* All Steps */}
                <div className="space-y-3">
                  {steps.map((step, index) => {
                    const isCompleted = userDecisions[step.id];
                    const isCurrent = index === currentStep;
                    const isUpcoming = index > currentStep;

                    return (
                      <div
                        key={step.id}
                        className={`p-3 rounded-lg border transition-all ${isCurrent
                          ? 'border-blue-300 bg-blue-50 shadow-sm'
                          : isCompleted
                            ? 'border-green-300 bg-green-50'
                            : 'border-gray-200 bg-white'
                          }`}
                      >
                        <div className="flex items-start space-x-3">
                          {/* Step Number and Status Icon */}
                          <div className="flex-shrink-0">
                            {isCompleted ? (
                              <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                                <CheckCircle size={14} className="text-white" />
                              </div>
                            ) : isCurrent ? (
                              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">{index + 1}</span>
                              </div>
                            ) : (
                              <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-gray-600">{index + 1}</span>
                              </div>
                            )}
                          </div>

                          {/* Step Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <h5 className={`font-medium ${isCurrent ? 'text-blue-800' : isCompleted ? 'text-green-800' : 'text-gray-700'
                                }`}>
                                {step.title}
                              </h5>
                              {isCurrent && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  Current
                                </span>
                              )}
                              {isCompleted && userDecisions[step.id] && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${userDecisions[step.id].correct
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                                  }`}>
                                  {userDecisions[step.id].correct ? 'Correct' : 'Incorrect'}
                                </span>
                              )}
                            </div>

                            <p className={`text-sm ${isCurrent ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-gray-600'
                              }`}>
                              {step.description}
                            </p>

                            {/* Step Type Badge */}
                            <div className="mt-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${step.type === 'analysis' || step.type === 'classification'
                                ? 'bg-yellow-100 text-yellow-800'
                                : step.type === 'remediation'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-blue-100 text-blue-800'
                                }`}>
                                {step.type === 'analysis' ? 'Question' :
                                  step.type === 'classification' ? 'Classification' :
                                    step.type === 'remediation' ? 'Remediation' :
                                      step.type === 'scan' ? 'Scan' : 'Action'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Progress Stats */}
                <div className="mt-4 bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Progress:</span>
                    <span className="font-medium text-gray-800">{completedSteps} / {steps.length} steps</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-gray-600">Accuracy:</span>
                    <span className="font-medium text-gray-800">{progress.accuracy}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(completedSteps / steps.length) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {renderFeedbackModal()}
    </div>
  );
};

// Wrapper that reuses the RealisticOSSimulator UI/behavior
const BrowserExtensionMalwareSimulator = (props) => {
  const { challenge, scenario: scenarioProp } = props;

  // Fetch steps from backend if not present on challenge
  const [fetchedSteps, setFetchedSteps] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    // If we already have steps on challenge or fetched, skip
    if ((challenge?.steps && Array.isArray(challenge.steps) && challenge.steps.length > 0) || fetchedSteps || loading) return;

    // If no challenge id, skip
    const cid = challenge?.id || challenge?._id || challenge?.challenge_id;
    if (!cid) return;

    // Fetch from backend using authenticated API client
    setLoading(true);
    // console.log('🔍 Fetching steps for challenge:', cid);

    getChallengeById(cid)
      .then(response => {
        // console.log('✅ Fetched challenge with steps:', response);
        let stepsData = null;

        // Handle different response formats
        if (response?.success && response?.data?.steps && Array.isArray(response.data.steps) && response.data.steps.length > 0) {
          stepsData = response.data.steps;
        } else if (response?.data?.steps && Array.isArray(response.data.steps) && response.data.steps.length > 0) {
          stepsData = response.data.steps;
        } else if (response?.steps && Array.isArray(response.steps) && response.steps.length > 0) {
          stepsData = response.steps;
        }

        if (stepsData) {
          // console.log('✅ Found steps:', stepsData.length, 'steps');
          setFetchedSteps(stepsData);
        } else {
          console.warn('⚠️ No steps found in response');
        }
      })
      .catch(err => {
        console.warn('⚠️ Failed to fetch steps:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [challenge, fetchedSteps, loading]);

  // Build a scenario (right-side steps) from challenge.steps or fetchedSteps
  let scenarioFromChallenge = null;
  const stepsSource = (challenge?.steps && Array.isArray(challenge.steps) && challenge.steps.length > 0)
    ? challenge.steps
    : fetchedSteps;

  // console.log('🔍 BrowserExtensionMalware - Steps Source:', {
  //   hasStepsOnChallenge: !!(challenge?.steps && Array.isArray(challenge.steps) && challenge.steps.length > 0),
  //   hasFetchedSteps: !!(fetchedSteps && Array.isArray(fetchedSteps) && fetchedSteps.length > 0),
  //   stepsSource: stepsSource,
  //   stepsSourceLength: stepsSource?.length
  // });

  if (stepsSource && Array.isArray(stepsSource) && stepsSource.length > 0) {
    // console.log('✅ Building scenario from', stepsSource.length, 'steps');
    const steps = [...stepsSource]
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      .map((s) => {
        // Parse content if it's a JSON string from DB
        let rawContent = s.content;
        let parsedContent = {};
        if (rawContent) {
          if (typeof rawContent === 'string') {
            try { parsedContent = JSON.parse(rawContent); } catch { parsedContent = {}; }
          } else if (typeof rawContent === 'object') {
            parsedContent = rawContent;
          }
        }

        // console.log('📋 Processing step:', {
        //   step_id: s.step_id || s.id,
        //   title: s.title,
        //   type: s.type,
        //   description: s.description,
        //   content: parsedContent
        // });

        // Consolidate fields from various shapes
        const question = s.question || parsedContent.question || s.description || '';
        const options = s.options || parsedContent.options || parsedContent.choices || [];
        const correctAnswer =
          s.correctAnswer !== undefined ? s.correctAnswer
            : parsedContent.correctAnswer !== undefined ? parsedContent.correctAnswer
              : parsedContent.correct_index !== undefined ? parsedContent.correct_index
                : parsedContent.correct_answer !== undefined ? parsedContent.correct_answer
                  : undefined;

        // Normalize type from DB values
        const normalizedType = (() => {
          const t = (s.type || '').toString().toLowerCase();
          if (t.includes('knowledge')) return 'analysis';
          if (t.includes('classif')) return 'classification';
          if (t.includes('simul')) return 'action';
          if (t === 'analysis' || t === 'classification' || t === 'action' || t === 'scan') return t;
          return options && options.length ? 'analysis' : 'action';
        })();

        const processedStep = {
          id: s.step_id || s.id,
          type: normalizedType,
          description: s.description || parsedContent.action || '',
          question,
          options: Array.isArray(options) ? options : [],
          correctAnswer: typeof correctAnswer === 'number' ? correctAnswer : undefined,
        };

        // console.log('✅ Processed step:', processedStep);
        return processedStep;
      });

    scenarioFromChallenge = {
      title: challenge.title,
      description: challenge.description,
      steps,
    };
    // console.log('✅ Final scenario:', scenarioFromChallenge);
  } else {
    console.warn('⚠️ No steps source available');
  }

  // Build a custom scenario with browser extensions for the malware challenge
  const browserExtensionScenario = scenarioFromChallenge ? {
    ...scenarioFromChallenge,
    // Add initial notification to guide user
    initialNotifications: [
      {
        id: 'malware-alert-1',
        type: 'warning',
        title: 'Security Alert',
        message: 'Suspicious browser activity detected. Check your browser extensions.'
      }
    ],
    // Add malicious background process for the extension
    backgroundProcesses: [
      {
        id: 'bg-malicious-extension',
        name: 'Super Ad Blocker Pro Helper',
        type: 'background',
        cpu: { mean: 45, jitter: 10 },
        memory: { mean: 89, jitter: 15 },
        disk: { mean: 2, jitter: 1 },
        network: { mean: 25, jitter: 10 },
        isRogue: true
      }
    ]
  } : null;

  const scenarioToUse = scenarioProp || browserExtensionScenario || scenarioFromChallenge || null;

  // Helper to build scenario from raw DB steps (for backend fetch fallback)
  const buildScenarioFromDbSteps = (rawSteps = []) => {
    const steps = [...rawSteps]
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      .map((s) => {
        let parsedContent = {};
        if (s.content) {
          if (typeof s.content === 'string') {
            try { parsedContent = JSON.parse(s.content); } catch { parsedContent = {}; }
          } else if (typeof s.content === 'object') {
            parsedContent = s.content;
          }
        }
        const question = s.question || parsedContent.question || s.description || '';
        const options = s.options || parsedContent.options || parsedContent.choices || [];
        const correctAnswer =
          s.correctAnswer !== undefined ? s.correctAnswer
            : parsedContent.correctAnswer !== undefined ? parsedContent.correctAnswer
              : parsedContent.correct_index !== undefined ? parsedContent.correct_index
                : parsedContent.correct_answer !== undefined ? parsedContent.correct_answer
                  : undefined;
        const normalizedType = (() => {
          const t = (s.type || '').toString().toLowerCase();
          if (t.includes('knowledge')) return 'analysis';
          if (t.includes('classif')) return 'classification';
          if (t.includes('simul')) return 'action';
          if (['analysis', 'classification', 'action', 'scan'].includes(t)) return t;
          return options && options.length ? 'analysis' : 'action';
        })();
        return {
          id: s.step_id || s.id,
          type: normalizedType,
          description: s.description || parsedContent.action || '',
          question,
          options: Array.isArray(options) ? options : [],
          correctAnswer: typeof correctAnswer === 'number' ? correctAnswer : undefined,
        };
      });
    return {
      title: challenge?.title,
      description: challenge?.description,
      steps,
    };
  };

  // If scenario is not provided and challenge doesn't carry steps, fetch from backend by challenge id
  const [scenarioLocal, setScenarioLocal] = React.useState(null);
  React.useEffect(() => {
    if (scenarioToUse || scenarioLocal) return;
    const cid = challenge?.id || challenge?._id || challenge?.challenge_id;
    if (!cid) return;
    const baseUrl = (process.env.REACT_APP_API_URL || '');
    const join = (a, b) => `${a.replace(/\/$/, '')}/${b.replace(/^\//, '')}`;
    const apiRoot = baseUrl ? (/\/api\/?$/.test(baseUrl) ? baseUrl.replace(/\/$/, '') : join(baseUrl, 'api')) : '/api';
    const makeUrl = (path) => join(apiRoot, path);
    const urlsToTry = [
      makeUrl(`challenges/${cid}/steps`),
      makeUrl(`challenges/${cid}`),
      makeUrl(`challenge-steps?challenge_id=${cid}`),
      makeUrl(`challenge_steps?challenge_id=${cid}`),
      makeUrl(`steps?challenge_id=${cid}`),
    ];
    (async () => {
      for (const url of urlsToTry) {
        try {
          const res = await fetch(url, { credentials: 'include' });
          if (!res.ok) continue;
          const data = await res.json();
          const rawSteps = data?.steps || data?.challenge?.steps || data?.items || data || [];
          if (Array.isArray(rawSteps) && rawSteps.length) {
            setScenarioLocal(buildScenarioFromDbSteps(rawSteps));
            break;
          }
        } catch (_) {
          // try next URL
        }
      }
    })();
  }, [scenarioToUse, scenarioLocal, challenge?.id, challenge?._id, challenge?.challenge_id]);

  // Local state for right-panel questionnaire
  const ReactRef = React; // keep React in scope
  const useState = React.useState;
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const scenarioEffective = scenarioToUse || scenarioLocal || null;
  const steps = scenarioEffective?.steps || [];
  const currentStep = steps[currentStepIndex] || null;
  const [answers, setAnswers] = useState({});

  // console.log('🎯 Right Panel State:', {
  //   scenarioToUse,
  //   scenarioLocal,
  //   scenarioEffective,
  //   stepsCount: steps.length,
  //   currentStepIndex,
  //   currentStep,
  //   allSteps: steps
  // });

  const handleSubmitAnswer = () => {
    if (!currentStep) return;
    // advance to next
    if (currentStepIndex < steps.length - 1) setCurrentStepIndex(currentStepIndex + 1);
    // else finished - optionally call onComplete
    if (currentStepIndex === steps.length - 1 && props.onComplete) {
      const correctCount = steps.reduce((acc, s, idx) => {
        const a = answers[s.id];
        return acc + (typeof s.correctAnswer === 'number' && a === s.correctAnswer ? 1 : 0);
      }, 0);
      props.onComplete({ correct: correctCount, total: steps.length });
    }
  };

  // console.log('🎯 Passing scenario to RealisticOS:', scenarioToUse);

  return (
    <div className="w-full min-h-[100dvh] flex flex-col lg:flex-row">
      <div className="flex-1 relative">
        <RealisticOSSimulator
          osType={"windows11"}
          fullScreen={false}
          challenge={challenge}
          scenarios={props.scenarios || []}
          scenario={scenarioToUse}
          onComplete={props.onComplete}
          onExit={props.onExit}
        />
      </div>

      {/* Right Panel - Questions & Assistance */}
      <div className="w-full lg:w-96 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-200 bg-blue-600 text-white font-semibold">Questions & Assistance</div>
        <div className="p-4 overflow-y-auto flex-1">
          {currentStep ? (
            <>
              {/* Header */}
              <div className="text-sm text-gray-600 mb-1">Step {currentStepIndex + 1}{steps.length ? ` / ${steps.length}` : ''}</div>
              <div className="text-base font-semibold mb-3 text-gray-900">
                {currentStep.question || currentStep.description || 'Follow the instruction'}
              </div>

              {/* Options for MCQ */}
              {Array.isArray(currentStep.options) && currentStep.options.length > 0 ? (
                <div className="space-y-2 mb-3">
                  {currentStep.options.map((opt, idx) => (
                    <label key={idx} className={`flex items-center border rounded px-3 py-2 text-sm cursor-pointer text-gray-900 ${answers[currentStep.id] === idx ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}>
                      <input
                        type="radio"
                        name={`step-${currentStep.id}`}
                        className="mr-2"
                        checked={answers[currentStep.id] === idx}
                        onChange={() => setAnswers({ ...answers, [currentStep.id]: idx })}
                      />
                      <span className="text-gray-900">{opt}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-900 mb-3">Complete the action on the left side desktop.</div>
              )}

              <button
                onClick={handleSubmitAnswer}
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700"
                disabled={Array.isArray(currentStep.options) && currentStep.options.length > 0 && answers[currentStep.id] === undefined}
              >
                {currentStepIndex < steps.length - 1 ? 'Submit Answer' : 'Finish'}
              </button>

              {/* Progress */}
              <div className="mt-6">
                <div className="text-sm font-medium text-gray-900 mb-2">Progress</div>
                <div className="space-y-2">
                  {steps.slice(0, currentStepIndex).map((s, i) => (
                    <div key={s.id} className="flex items-center text-sm text-gray-900">
                      <span className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center mr-2 text-xs">✓</span>
                      <span className="truncate">{s.description || s.title || s.question}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="text-sm text-gray-900">No steps found for this challenge.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrowserExtensionMalwareSimulator;